<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Modal pour demander le numéro de téléphone -->
    <div
      v-if="showPhoneModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300"
        :class="showPhoneModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'"
      >
        <div class="p-6 sm:p-8">
          <div class="text-center mb-6">
            <div
              class="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <i class="fa-solid fa-phone text-white text-2xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Numéro de téléphone requis</h3>
            <p class="text-gray-600">
              Pour continuer l'entraînement, veuillez renseigner votre numéro de téléphone.
            </p>
          </div>

          <div class="space-y-4">
            <div class="group">
              <label for="phone-modal" class="block text-sm font-semibold text-gray-700 mb-2">
                <div class="flex items-center">
                  <i class="fa-solid fa-mobile-alt text-primary-500 mr-2"></i>
                  Numéro de téléphone
                </div>
              </label>
              <div class="relative">
                <input
                  id="phone-modal"
                  v-model="phoneForm.phone"
                  type="tel"
                  placeholder="6 XX XX XX XX"
                  class="w-full px-4 py-4 pl-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500': phoneForm.error,
                  }"
                  @input="phoneForm.error = ''"
                />
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i class="fa-solid fa-phone text-gray-400"></i>
                </div>
              </div>
              <p v-if="phoneForm.error" class="mt-2 text-sm text-red-600 flex items-center">
                <i class="fa-solid fa-exclamation-circle mr-1"></i>
                {{ phoneForm.error }}
              </p>
              <p class="mt-2 text-xs text-gray-500">
                Format: 6XXXXXXXX (9 chiffres commençant par 6)
              </p>
            </div>

            <div class="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                @click="submitPhone"
                :disabled="phoneForm.isSubmitting"
                class="flex-1 relative overflow-hidden group bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl"
              >
                <span class="relative z-10 flex items-center justify-center">
                  <i
                    v-if="phoneForm.isSubmitting"
                    class="fa-solid fa-spinner fa-spin mr-2"
                  ></i>
                  <i v-else class="fa-solid fa-check mr-2"></i>
                  {{ phoneForm.isSubmitting ? 'Enregistrement...' : 'Valider' }}
                </span>
              </button>

              <button
                @click="cancelPhoneModal"
                :disabled="phoneForm.isSubmitting"
                class="sm:flex-none relative overflow-hidden group bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 disabled:opacity-50 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                <span class="relative z-10 flex items-center justify-center">
                  <i class="fa-solid fa-times mr-2"></i>
                  Annuler
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Header avec timer -->
    <div
      class="bg-white border-b-2 border-gray-200 sticky top-0 z-40"
      :class="{ 'pt-[70px]': true }"
    >
      <div class="max-w-6xl mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="$router.back()"
              class="group cursor-pointer relative overflow-hidden bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
            >
              <span class="relative z-10">
                <i class="fa-solid fa-arrow-left text-lg"></i>
              </span>
              <div
                class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
              ></div>
            </button>
            <div>
              <h1 class="text-lg font-semibold text-gray-900">
                {{ currentExercice?.title }}
              </h1>
            </div>
          </div>

          <!-- Timer -->
          <div class="flex items-center space-x-3">
            <div class="flex items-center space-x-2 px-4 py-2 rounded-lg text-green-600">
              <i class="fa-solid fa-clock"></i>
              <span class="font-mono font-semibold text-xl">
                {{ formatTime(timeRemaining) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <LoadingState v-if="isLoading"></LoadingState>

    <!-- Error state -->
    <div v-else-if="error" class="max-w-4xl mx-auto px-4 py-12">
      <div class="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
        <i class="fa-solid fa-exclamation-triangle text-red-500 text-3xl mb-4"></i>
        <h3 class="text-lg font-semibold text-red-700 mb-2">Erreur</h3>
        <p class="text-red-600 mb-4">{{ error }}</p>
        <button
          @click="retry"
          class="bg-red-500 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200 hover:bg-red-600"
        >
          Réessayer
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div v-else class="max-w-6xl mx-auto px-4 py-6">
      <div
        v-if="['MCQ'].includes(currentQuestion?.question_type)"
        class="grid grid-cols-1 lg:grid-cols-4 gap-x-6 gap-y-1"
      >
        <!-- Ressources -->
        <div class="lg:col-span-2 items-start justify-between">
          <div v-for="(ressource, index) in currentQuestion.ressouces" :key="index" class="w-full">
            <div v-if="ressource.image_file" class="relative border-2 border-gray-200 rounded-lg">
              <img
                :src="ressource.image_file"
                alt="Ressource visuelle"
                class="w-full h-auto duration-300"
              />
            </div>

            <div v-if="ressource.audio_file" class="mt-4">
              <AudioPlayer
                :src="ressource.audio_file"
                :practiceType="practiceType"
                class="rounded-2xl overflow-hidden shadow-lg"
              />
            </div>

            <div
              v-if="ressource.audio_file"
              class="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl p-4"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1"
                >
                  <i class="fa-solid fa-info-circle text-white text-sm"></i>
                </div>
                <div>
                  <p class="text-amber-800">{{ ressource.title }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Question et réponses -->
        <div class="lg:col-span-2">
          <div
            v-if="currentQuestion"
            class="bg-white border-2 border-gray-200 rounded-2xl px-4 py-3"
          >
            <div class="mb-3">
              <div class="prose prose-gray max-w-none flex justify-end">
                <p class="text-gray-800 text-lg leading-relaxed"></p>
                <div
                  v-if="!isSubmittingAnswer && currentAttemptIsCorrect != null"
                  :class="currentAttemptIsCorrect ? 'text-green-500' : 'text-red-500'"
                >
                  {{ currentAttemptIsCorrect ? 'Correct' : 'Incorrect' }}
                </div>
                <div v-if="isSubmittingAnswer"><i class="fa-solid fa-spinner fa-spin"></i></div>
              </div>
            </div>

            <!-- Choix multiples -->
            <div class="space-y-4">
              <div v-if="['MCQ'].includes(currentQuestion.question_type)">
                <div class="space-y-3">
                  <div
                    v-for="choice in currentQuestion.choices"
                    :key="choice.slug"
                    @click="selectChoice(choice)"
                    class="border-2 rounded-2xl p-3 cursor-pointer transition-all duration-200 hover:bg-gray-50"
                    :class="getChoiceClass(choice)"
                  >
                    <div class="flex items-center space-x-3">
                      <div class="flex-shrink-0 mt-1">
                        <div
                          class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200"
                          :class="
                            isChoiceSelected(choice)
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-gray-300'
                          "
                        >
                          <div
                            v-if="isChoiceSelected(choice)"
                            class="w-2 h-2 bg-white rounded-full"
                          ></div>
                        </div>
                      </div>
                      <div class="ms-2">
                        <p class="text-gray-800 font-medium mt-1">
                          {{ choice.choice_text }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation -->
            <div class="flex items-center justify-end mt-8 pt-6 border-t-2 border-gray-200">
              <button
                id="autre_question"
                @click="nextQuestion"
                class="group relative cursor-pointer overflow-hidden flex items-center space-x-3 px-6 py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span>Charger une autre question</span>
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Questions d'expression -->
      <div v-else>
        <div
          class="p-5 mb-4 border-yellow-200 border-2 rounded-xl bg-yellow-50/50 flex items-center flex justify-between gap-3"
        >
          <div class="flex items-center gap-2 text-yellow-600">
            <i class="fa-solid fa-warning text-3xl text-yellow-500"></i> la correction des exercices
            d'expression n'est pas disponible sans abonnement
          </div>
          <div>
            <router-link to="/packages" class="text-gray-700"
              >Acheter un package <i class="fa-solid fa-arrow-right"></i
            ></router-link>
          </div>
        </div>
        <div
          class="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-4 flex flex-col gap-3 mb-8 shadow-xl shadow-gray-200/50"
        >
          <div
            class="p-5 flex flex-col border-primary-200 border-2 rounded-xl bg-primary-50/50 flex items-start gap-3"
          >
            <span class="px-5 py-1 rounded-lg bg-primary-400">Sujet: </span>
            <div>
              {{ getQuestionTitle(currentQuestion?.question_text) }}
            </div>
          </div>

          <div
            class=""
            v-if="['open_text', 'essay', 'audio'].includes(currentQuestion?.question_type)"
          >
            <div v-if="['audio'].includes(currentQuestion?.question_type)" class="w-full">
              <VoiceRecorder
                :question-slug="currentQuestion.slug"
                :max-duration="180"
                @recording-complete="onRecordingComplete"
                class="mb-6"
              />
            </div>
            <textarea
              v-else
              rows="10"
              class="w-full border border-2 border-gray-200 bg-gray-50 p-5 rounded-xl"
              placeholder="Ecrivez ici..."
            ></textarea>
          </div>
          <div class="flex justify-end">
            <button
              @click="nextQuestion"
              class="group relative cursor-pointer overflow-hidden flex items-center space-x-3 px-6 py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>Essayer à nouveau</span>
              <i class="fa-solid fa-rotate-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useLanguageStore } from '@/stores/language'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'
import AudioPlayer from '@/components/common/audioPlayer.vue'
import { useAuthStore } from '@/stores/auth.js'
import LoadingState from '@/components/exercices/LoadingState.vue'
import VoiceRecorder from '@/components/exercices/voiceRecorder.vue'

const props = defineProps({
  slug_exam: {
    type: String,
    required: true,
  },
})

const store = useLanguageStore()
const authStore = useAuthStore()

const { success, error: showError, warning, info } = useToast()
const router = useRouter()

// Reactive state
const isLoading = ref(true)
const error = ref(null)
const currentQuestionIndex = ref(0)
const selectedChoices = ref({})
const writtenAnswers = ref({})
const writtenAnswer = ref('')
const isRecording = ref(false)
const showExitModal = ref(false)
const showFinishModal = ref(false)
const currentAttemptIsCorrect = ref(null)
const isSubmittingAnswer = ref(false)
const showPhoneModal = ref(false)

// Formulaire de téléphone
const phoneForm = ref({
  phone: '',
  error: '',
  isSubmitting: false,
})

// Timer
const timeRemaining = ref(0)
const timerInterval = ref(null)

// Computed
const currentExercice = computed(() => store.currentExercice)
const questions = computed(() => store.currentQuestions)
const userAnswers = computed(() => store.userAnswers)
const totalQuestions = computed(() => questions.value.length)
const currentQuestion = computed(() => store.currentTrainingQuestion)
const practiceType = computed(() => store.practiceType)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const ExerciceType = computed(() => store.currentExercice?.type_exam)

const hasTimeLimit = computed(() => {
  return (
    practiceType.value === 'exam' &&
    currentExercice.value?.time_limit &&
    currentExercice.value.time_limit > 0
  )
})

const progress = computed(() => {
  if (totalQuestions.value === 0) return 0
  return (userAnswers.value.length / totalQuestions.value) * 100
})

// Validation du numéro camerounais
const validateCameroonianPhone = (phone) => {
  // Nettoyer le numéro
  const cleanPhone = phone.replace(/\s+/g, '').replace(/^(\+237|237)/, '')

  // Vérifier le format: doit commencer par 6 et avoir 9 chiffres
  const phoneRegex = /^6[0-9]{8}$/

  if (!cleanPhone) {
    return { valid: false, message: 'Le numéro de téléphone est requis' }
  }

  if (!phoneRegex.test(cleanPhone)) {
    return {
      valid: false,
      message: 'Format invalide. Le numéro doit commencer par 6 et contenir 9 chiffres (ex: 6 XX XX XX XX)',
    }
  }

  return { valid: true, cleanPhone: cleanPhone }
}

// Vérifier si l'utilisateur a un numéro de téléphone
const checkUserPhone = async () => {
  if (!isAuthenticated.value) {
    return true // Pas besoin de vérifier si non connecté
  }

  await authStore.fetchUser()

  if (!authStore.user?.phone) {
    showPhoneModal.value = true
    return false
  }

  return true
}

// Soumettre le numéro de téléphone
const submitPhone = async () => {
  phoneForm.value.error = ''

  // Validation
  const validation = validateCameroonianPhone(phoneForm.value.phone)
  if (!validation.valid) {
    phoneForm.value.error = validation.message
    return
  }

  try {
    phoneForm.value.isSubmitting = true

    // Mettre à jour le profil avec uniquement le téléphone
    const result = await authStore.updateProfile({
      phone: validation.cleanPhone,
    })

    if (result.success) {
      success('Numéro de téléphone enregistré avec succès!')
      showPhoneModal.value = false
      phoneForm.value.phone = ''

      // Rafraîchir les données utilisateur
      await authStore.fetchUser()
      await loadQuestionData()
    } else {
      phoneForm.value.error = result.error || 'Erreur lors de l\'enregistrement'
    }
  } catch (err) {
    phoneForm.value.error = 'Une erreur est survenue. Veuillez réessayer.'
    console.error('Erreur lors de la mise à jour du téléphone:', err)
  } finally {
    phoneForm.value.isSubmitting = false
  }
}

// Annuler la modal de téléphone
const cancelPhoneModal = () => {
  showPhoneModal.value = false
  phoneForm.value.phone = ''
  phoneForm.value.error = ''
  router.push({ name: 'dashboard-exercises' })
}

// Methods
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

const getQuestionTypeLabel = (type) => {
  const labels = {
    MCQ: 'Choix multiple',
    TRUE_FALSE: 'Vrai/Faux',
    FILL_BLANK: 'Texte à trou',
    WRITTEN: 'Réponse écrite',
    SPEAKING: 'Expression orale',
  }
  return labels[type] || type
}

const getQuestionButtonClass = (index) => {
  const isAnswered = userAnswers.value.some(
    (answer) => questions.value[index] && answer.questionSlug === questions.value[index].slug,
  )
  const isCurrent = index === currentQuestionIndex.value

  if (isCurrent) {
    return 'border-primary-500 bg-primary-500 text-white'
  } else if (isAnswered) {
    return 'border-green-500 bg-green-500 text-white'
  } else {
    return 'border-gray-300 text-gray-600 hover:border-gray-400'
  }
}

const getChoiceClass = (choice) => {
  if (practiceType.value === 'exam') {
    return isChoiceSelected(choice)
      ? 'border-primary-500 bg-primary-50'
      : 'border-gray-300 hover:border-gray-400'
  } else {
    if (currentAttemptIsCorrect.value != null) {
      if (isChoiceSelected(choice) && currentAttemptIsCorrect.value)
        return 'border-green-500 bg-green-50'
      else if (isChoiceSelected(choice) && !currentAttemptIsCorrect.value)
        return 'border-red-500 bg-red-50'
      else if (!isChoiceSelected(choice) && choice?.is_correct)
        return 'border-green-500 bg-green-50'
      else return 'border-gray-300 hover:border-gray-400'
    } else {
      return isChoiceSelected(choice)
        ? 'border-primary-500 bg-primary-50'
        : 'border-gray-300 hover:border-gray-400'
    }
  }
}

const isChoiceSelected = (choice) => {
  return selectedChoices.value[currentQuestion.value.slug] === choice.slug
}

const selectChoice = async (choice) => {
  if (!currentQuestion.value) return

  selectedChoices.value[currentQuestion.value.slug] = choice.slug
  currentAttemptIsCorrect.value = choice?.is_correct
  stopTimer()
}

const updateWrittenAnswer = () => {
  writtenAnswers.value[currentQuestion.value.slug] = writtenAnswer.value
}

const saveWrittenAnswer = async () => {
  if (!writtenAnswer.value.trim() || !currentQuestion.value) return
  success('Réponse écrite sauvegardée')
}

const toggleRecording = () => {
  isRecording.value = !isRecording.value
  if (isRecording.value) {
    info('Enregistrement démarré')
  } else {
    info('Enregistrement arrêté')
  }
}

const goToQuestion = (index) => {
  if (index >= 0 && index < totalQuestions.value) {
    currentQuestionIndex.value = index
    loadQuestionData()
  }
}

const previousQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--
    loadQuestionData()
  }
}

const nextQuestion = async () => {
  isSubmittingAnswer.value = true
  await loadQuestionData()
  isSubmittingAnswer.value = false
  currentAttemptIsCorrect.value = null
  currentQuestionIndex.value++
  startTimer()
}

const loadQuestionData = async () => {
  try {
    isLoading.value = true
    await store.getRandomQuestion(props?.slug_exam)
  } catch (e) {
    console.error('Erreur lors du chargement de la question:', e)
  }
  isLoading.value = false
}

const startTimer = () => {
  timeRemaining.value = 0

  timerInterval.value = setInterval(() => {
    timeRemaining.value++
  }, 1000)
}

const stopTimer = () => {
  clearInterval(timerInterval.value)
}

const exit = () => {
  router.push({ name: 'dashboard-exercises' })
}

const exitExercice = () => {
  const attempt_slug = store.currentAttempt.slug
  clearInterval(timerInterval.value)
  store.resetCurrentExercice()
  router.push('/exercice-results/' + attempt_slug)
}

const confirmFinish = () => {
  showFinishModal.value = true
}

const getQuestionTitle = (question_text) => {
  try {
    const jsonObject = eval('(' + question_text + ')')
    return jsonObject?.titre || jsonObject?.sujet
  } catch (e) {
    return question_text
  }
}

const finishExercice = async () => {
  try {
    clearInterval(timerInterval.value)
    router.push('/exercice-results/' + store.currentAttempt?.slug || props.slug)
  } catch (err) {
    showError('Erreur lors de la finalisation')
    console.error(err)
  }
}

const openResource = (url) => {
  window.open(url, '_blank')
}

const loadExercice = async () => {
  try {
    isLoading.value = true
    error.value = null

    // Démarrer l'exercice si pas déjà fait
    if (!store.currentExercice || store.currentExercice.slug !== props.slug) {
      const result = await store.startExercice(props.slug)

      if (!result.success) {
        throw new Error(result.error)
      }
    }

    if (questions.value.length === 0) {
      throw new Error('Aucune question trouvée pour cet exercice')
    }

    // Démarrer le timer
    startTimer()

    // Charger les données de la première question
    loadQuestionData()
  } catch (err) {
    error.value = err.message || "Erreur lors du chargement de l'exercice"
    showError(error.value)
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const retry = () => {
  loadExercice()
}

const onRecordingComplete = (data) => {
  console.log('Enregistrement terminé:', data)
}

// Watchers
watch(currentQuestionIndex, () => {
  loadQuestionData()
})

// Lifecycle
onMounted(async () => {
  // Vérifier d'abord si l'utilisateur a un numéro de téléphone
  const hasPhone = await checkUserPhone()

  if (hasPhone) {
    await loadQuestionData()
    startTimer()
  } else {
    isLoading.value = false
  }
})

onUnmounted(() => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
  }
})

// Prevent accidental page refresh
onMounted(() => {
  const handleBeforeUnload = (e) => {
    if (store.currentExercice && !showPhoneModal.value) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  window.addEventListener('beforeunload', handleBeforeUnload)

  onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  })
})
</script>

<style scoped>
.prose {
  max-width: none;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
